import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  FlatList,
  ActivityIndicator,
  Platform,
  TextInput,
  Modal,
  Image,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { overlayPickerStyles } from '../components/OverlayPanel';
import {
  Header,
  ScreenLayout,
  colors,
  spacing,
  typography,
} from '../components';
import { DateRangePicker } from '../components/DateRangePicker';
import { ReportContactPicker, ReportContactSelection } from '../components/ReportContactPicker';
import { ReportsPerformancePanel } from '../components/reports/ReportsPerformancePanel';
import { groupReportItemsByContact, ReportContactGroupRow, ReportContactGroup } from '../components/reports/ReportContactGroups';
import { formatReportDateTime, formatReportDuration, formatReportTime } from '../utils/reportFormat';
import { VisitTiming } from '../components/visits/VisitTiming';
import { VisitAnalyticsPanel } from '../components/visits/VisitAnalyticsPanel';
import { VisitTimingData, VisitMetrics, visitPrimaryTime } from '../utils/visitTiming';
import { ReportBreakLine } from '../components/reports/ReportContactGroups';
import { VoiceNotePlayer } from '../components/conversation-cards';
import { AreaChartCard } from './dashboard/AreaChartCard';
import { DonutChart } from './dashboard/DonutChart';
import { BarChartCard } from './dashboard/BarChartCard';
import { KpiTile } from './dashboard/KpiTile';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { usePermissions } from '../hooks/usePermissions';
import { apiService, ReportsPerformance } from '../services/api';
import { getChannelIcon, getChannelColor } from '../utils/channelIcons';
import { useThemedStyles } from '../hooks/useThemedStyles';

type ReportTab = 'all' | 'messages' | 'calls' | 'visits' | 'performance';
type DatePreset = 'today' | '7d' | '30d' | 'custom';
type SortKey = 'time' | 'name' | 'status';
type SortDirection = 'asc' | 'desc';

const SORT_KEYS: SortKey[] = ['time', 'name', 'status'];

const MESSAGE_TYPES = [
  'text', 'image', 'video', 'audio', 'document', 'location', 'location_request',
  'contact', 'sticker', 'template', 'interactive', 'button', 'calendar', 'reaction', 'unsupported',
] as const;

const CALL_STATUSES = ['ringing', 'answered', 'completed', 'not-responded', 'cancelled', 'ended', 'failed'] as const;
const VISIT_STATUSES = ['pending', 'scheduled', 'in_progress', 'completed', 'missed', 'cancelled', 'rescheduled'] as const;

const buildPresetRange = (preset: Exclude<DatePreset, 'custom'>): { startDate: Date; endDate: Date } => {
  const now = new Date();
  const endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  const daysBack = preset === 'today' ? 0 : preset === '7d' ? 6 : 29;
  const startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysBack, 0, 0, 0, 0);
  return { startDate, endDate };
};

interface ContactMessageGroup {
  contactId: string;
  conversationId: string;
  contactName?: string;
  contactPhone?: string;
  contactAvatar?: string;
  channel: string;
  messageCount: number;
  lastMessage: { messageId: string; type: string; direction: string; status: string; content: string; timestamp: string; agentName?: string; departmentName?: string };
  typeBreakdown: { type: string; count: number }[];
}

interface CallRow {
  _id: string;
  contactId?: string;
  contactName?: string;
  phoneNumber: string;
  direction: 'inbound' | 'outbound';
  status: string;
  duration?: number;
  startedAt: string;
  answeredAt?: string;
  endedAt?: string;
  assignedTo?: string;
  agentName?: string;
  departmentName?: string;
  provider?: string;
  breakBeforeSeconds?: number | null;
}

interface VisitRow extends VisitTimingData {
  _id: string;
  contactId?: string;
  contactName?: string;
  campaignId?: string;
  campaignName?: string;
  status: string;
  priority: string;
  scheduledFor?: string;
  startedAt?: string;
  endedAt?: string;
  actualDurationMinutes?: number;
  assignedTo?: string;
  departmentId?: string;
  agentName?: string;
  departmentName?: string;
  outcomeId?: string;
  outcomeLabel?: string;
  outcomeResultType?: string;
}

interface FlatMessageRow {
  messageId: string;
  conversationId: string;
  contactId: string;
  contactName?: string;
  contactPhone?: string;
  contactAvatar?: string;
  channel: string;
  type: string;
  direction: string;
  status: string;
  content: string;
  timestamp: string;
  agentName?: string;
  departmentName?: string;
  breakBeforeSeconds?: number | null;
}

/** One row in the combined "All" timeline tab — a flat, chronologically-merged view
 * of individual messages and calls (never grouped-by-contact), addressing the
 * complaint that the Messages tab's default contact-grouping made it look like data
 * was missing/the list stopped early (free space, no visible rows) once every
 * distinct contact had a row — this tab always shows every record. */
type TimelineItem =
  | { kind: 'message'; timestamp: string; data: FlatMessageRow }
  | { kind: 'call'; timestamp: string; data: CallRow }
  | { kind: 'visit'; timestamp: string; data: VisitRow };

interface FilterUser { userId: string; name: string; email?: string }

interface DailyPoint { date: string; count: number }

interface ScopedSummary {
  messages: { total: number; inbound: number; outbound: number; dailyVolume: DailyPoint[] };
  calls: { total: number; inbound: number; outbound: number; completed: number; dailyVolume: DailyPoint[] };
  visits: { total: number; completed: number; missed: number; cancelled: number; dailyVolume: DailyPoint[]; outcomeBreakdown: { outcomeLabel: string; count: number }[];
    analytics?: VisitMetrics; byAgent?: Array<VisitMetrics & { userId?: string; departmentId?: string; agentName?: string; departmentName?: string }>;
    statusBreakdown?: Array<{ status: string; count: number }>;
  };
  typeBreakdown: { type: string; count: number }[];
  channelBreakdown: { channel: string; count: number }[];
}

const useCallRecording = (callId: string | null) => {
  const [state, setState] = useState<'idle' | 'loading' | 'ready' | 'unavailable' | 'error'>('idle');
  const [localUri, setLocalUri] = useState<string | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  // Selecting a different call previously left `state`/`localUri` at whatever the
  // PREVIOUS call's recording had loaded to ('ready' + its blob URL) — the cleanup
  // below revoked the old blob URL but never reset this hook's own state back to
  // 'idle', so CallRecordingCell kept rendering VoiceNotePlayer with a stale
  // mediaUrl instead of showing a fresh "Play" prompt for the newly selected call,
  // and the already-decoded <audio> element kept playing the old recording since
  // browsers don't stop audio just because its blob: URL was revoked out from
  // under it. Resetting on every callId change forces a clean remount.
  useEffect(() => {
    setState('idle');
    setLocalUri(null);
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [callId]);

  const load = useCallback(async () => {
    if (!callId || state === 'loading' || state === 'ready') return;
    setState('loading');
    try {
      const token = apiService.getAuthToken();
      const url = `${apiService.getBaseURL()}/api/phone/calls/${callId}/recording`;
      const response = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : undefined });
      if (response.status === 404 || response.status === 409) {
        setState('unavailable');
        return;
      }
      if (!response.ok) {
        setState('error');
        return;
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      objectUrlRef.current = objectUrl;
      setLocalUri(objectUrl);
      setState('ready');
    } catch (err) {
      console.warn('[ReportsScreen] Failed to load call recording:', err);
      setState('error');
    }
  }, [callId, state]);

  return { state, localUri, load };
};

const CallRecordingCell: React.FC<{ call: CallRow }> = ({ call }) => {
  const styles = useThemedStyles(getStyles);
  const { t, locale } = useLocale();
  const recording = useCallRecording(call.status === 'completed' ? call._id : null);

  if (call.status !== 'completed') return <Text style={styles.mutedCell}>—</Text>;
  if (recording.state === 'idle') {
    return (
      <TouchableOpacity onPress={recording.load} style={styles.inlineActionBtn}>
        <Ionicons name="play-circle-outline" size={16} color={colors.primary as string} />
        <Text style={styles.inlineActionText}>{t('reports.calls.play')}</Text>
      </TouchableOpacity>
    );
  }
  if (recording.state === 'loading') return <ActivityIndicator size="small" color={colors.primary as string} />;
  if (recording.state === 'unavailable') return <Text style={styles.mutedCell}>{t('reports.calls.noRecording')}</Text>;
  if (recording.state === 'error') return <Text style={styles.mutedCell}>{t('reports.calls.recordingError')}</Text>;
  return (
    <View style={styles.recordingCellReady}>
      <VoiceNotePlayer mediaUrl={recording.localUri || ''} isOutgoing={false} />
    </View>
  );
};

/** Row-position number shown per the current sort — always 1-based and reflects the
 * CURRENTLY sorted/filtered order, not any stable underlying id, so it re-numbers
 * whenever the sort key/direction or filters change. */
const IndexBadge: React.FC<{ index: number }> = ({ index }) => {
  const styles = useThemedStyles(getStyles);
  return (
  <View style={styles.indexBadge}>
    <Text style={styles.indexBadgeText}>{index + 1}</Text>
  </View>
);
};

const ContactAvatar: React.FC<{ name?: string; avatar?: string; channel: string; size?: number }> = ({ name, avatar, channel, size = 36 }) => {
  const styles = useThemedStyles(getStyles);
  const channelColor = getChannelColor(channel);
  const initial = (name || '?').trim().charAt(0).toUpperCase();
  return (
    <View style={[styles.avatarWrap, { width: size, height: size, borderRadius: size / 2 }]}>
      {avatar ? (
        <Image source={{ uri: avatar }} style={{ width: size, height: size, borderRadius: size / 2 }} />
      ) : (
        <View style={[styles.avatarFallback, { width: size, height: size, borderRadius: size / 2, backgroundColor: channelColor + '30' }]}>
          <Text style={[styles.avatarFallbackText, { color: channelColor, fontSize: size * 0.4 }]}>{initial}</Text>
        </View>
      )}
      <View style={[styles.avatarChannelBadge, { backgroundColor: channelColor }]}>
        <Ionicons name={getChannelIcon(channel) as any} size={9} color="#FFFFFF" />
      </View>
    </View>
  );
};

/** Right-pane detail view for the selected contact group — mirrors CallHistoryScreen's
 * selected-row detail-panel pattern (ScreenLayout rightPaneContent="custom"). The
 * "open conversation" icon is the actual drill-down per the product decision (jump to
 * the real ConversationScreen rather than re-showing message detail here). */
const ContactGroupDetailsPanel: React.FC<{ group: ContactMessageGroup; onOpenConversation: () => void }> = ({ group, onOpenConversation }) => {
  const styles = useThemedStyles(getStyles);
  const { t, locale } = useLocale();
  return (
    <View style={styles.detailsPanel}>
      <View style={styles.detailsPanelHeader}>
        <ContactAvatar name={group.contactName} avatar={group.contactAvatar} channel={group.channel} size={48} />
        <Text style={styles.detailsPanelName} numberOfLines={1}>{group.contactName || group.contactPhone || group.contactId}</Text>
        {!!group.contactPhone && <Text style={styles.detailsPanelSub}>{group.contactPhone}</Text>}
        <TouchableOpacity style={styles.openConversationBtn} onPress={onOpenConversation}>
          <Ionicons name="chatbubble-ellipses-outline" size={14} color="#FFFFFF" />
          <Text style={styles.openConversationBtnText}>{t('reports.openConversation')}</Text>
        </TouchableOpacity>
      </View>
      <View style={styles.detailsPanelDivider} />
      <View style={styles.detailsRow}>
        <Text style={styles.detailsLabel}>{t('reports.filters.channel')}</Text>
        <View style={[styles.detailsChannelChip, { backgroundColor: getChannelColor(group.channel) + '20' }]}>
          <Ionicons name={getChannelIcon(group.channel) as any} size={12} color={getChannelColor(group.channel)} />
          <Text style={[styles.detailsChannelChipText, { color: getChannelColor(group.channel) }]}>{group.channel}</Text>
        </View>
      </View>
      {!!group.lastMessage.agentName && (
        <View style={styles.detailsRow}>
          <Text style={styles.detailsLabel}>{t('reports.filters.user')}</Text>
          <Text style={styles.detailsValue}>{group.lastMessage.agentName}{group.lastMessage.departmentName ? ` · ${group.lastMessage.departmentName}` : ''}</Text>
        </View>
      )}
      <View style={styles.detailsRow}>
        <Text style={styles.detailsLabel}>{t('reports.grouped.messageCount')}</Text>
        <Text style={styles.detailsValue}>{group.messageCount}</Text>
      </View>
      <View style={styles.detailsRow}>
        <Text style={styles.detailsLabel}>{t('reports.filters.startDate')}</Text>
        <Text style={styles.detailsValue}>{formatReportDateTime(group.lastMessage.timestamp, locale)}</Text>
      </View>
      <View style={styles.detailsPanelDivider} />
      <Text style={styles.detailsLabel}>{t('reports.grouped.lastMessage')}</Text>
      <Text style={styles.detailsSecondaryLine}>{group.lastMessage.type} · {group.lastMessage.direction} · {group.lastMessage.status}</Text>
      <Text style={styles.detailsContent}>{group.lastMessage.content}</Text>
      {group.typeBreakdown.length > 1 && (
        <>
          <View style={styles.detailsPanelDivider} />
          <Text style={styles.detailsLabel}>{t('reports.charts.typeBreakdown')}</Text>
          {group.typeBreakdown.map((tb) => (
            <View key={tb.type} style={styles.detailsRow}>
              <Text style={styles.detailsValue}>{tb.type}</Text>
              <Text style={styles.detailsLabel}>{tb.count}</Text>
            </View>
          ))}
        </>
      )}
    </View>
  );
};

const CallDetailsPanel: React.FC<{ call: CallRow }> = ({ call }) => {
  const styles = useThemedStyles(getStyles);
  const { t, locale } = useLocale();
  return (
    <View style={styles.detailsPanel}>
      <View style={styles.detailsPanelHeader}>
        <ContactAvatar name={call.contactName} channel="phone" size={48} />
        <Text style={styles.detailsPanelName} numberOfLines={1}>{call.contactName || call.phoneNumber}</Text>
        <Text style={styles.detailsPanelSub}>{call.phoneNumber}</Text>
      </View>
      <View style={styles.detailsPanelDivider} />
      {!!call.agentName && (
        <View style={styles.detailsRow}>
          <Text style={styles.detailsLabel}>{t('reports.filters.user')}</Text>
          <Text style={styles.detailsValue}>{call.agentName}{call.departmentName ? ` · ${call.departmentName}` : ''}</Text>
        </View>
      )}
      <View style={styles.detailsRow}>
        <Text style={styles.detailsLabel}>{t('reports.calls.direction')}</Text>
        <Text style={styles.detailsValue}>{call.direction}</Text>
      </View>
      <View style={styles.detailsRow}>
        <Text style={styles.detailsLabel}>{t('reports.calls.status')}</Text>
        <Text style={styles.detailsValue}>{call.status}</Text>
      </View>
      <View style={styles.detailsRow}>
        <Text style={styles.detailsLabel}>{t('reports.calls.duration')}</Text>
        <Text style={styles.detailsValue}>{call.duration ?? 0}s</Text>
      </View>
      <View style={styles.detailsRow}>
        <Text style={styles.detailsLabel}>{t('reports.calls.startedAt')}</Text>
        <Text style={styles.detailsValue}>{formatReportDateTime(call.startedAt, locale)}</Text>
      </View>
      {!!call.answeredAt && (
        <View style={styles.detailsRow}>
          <Text style={styles.detailsLabel}>{t('reports.calls.answeredAt')}</Text>
          <Text style={styles.detailsValue}>{formatReportDateTime(call.answeredAt, locale)}</Text>
        </View>
      )}
      <View style={styles.detailsRow}>
        <Text style={styles.detailsLabel}>{t('reports.calls.endedAt')}</Text>
        <Text style={styles.detailsValue}>{call.endedAt ? formatReportDateTime(call.endedAt, locale) : '—'}</Text>
      </View>
      {call.breakBeforeSeconds !== undefined && (
        <View style={styles.detailsRow}>
          <Text style={styles.detailsLabel}>{t('reports.break.label')}</Text>
          <ReportBreakLine seconds={call.breakBeforeSeconds} />
        </View>
      )}
      <View style={styles.detailsPanelDivider} />
      <CallRecordingCell call={call} />
    </View>
  );
};

const VisitDetailsPanel: React.FC<{ visit: VisitRow }> = ({ visit }) => {
  const styles = useThemedStyles(getStyles);
  const { t, locale } = useLocale();
  return (
    <View style={styles.detailsPanel}>
      <View style={styles.detailsPanelHeader}>
        <ContactAvatar name={visit.contactName} channel="visit" size={48} />
        <Text style={styles.detailsPanelName} numberOfLines={1}>{visit.contactName || visit.contactId}</Text>
        {!!visit.campaignName && <Text style={styles.detailsPanelSub}>{visit.campaignName}</Text>}
      </View>
      <View style={styles.detailsPanelDivider} />
      {!!visit.agentName && (
        <View style={styles.detailsRow}>
          <Text style={styles.detailsLabel}>{t('reports.filters.user')}</Text>
          <Text style={styles.detailsValue}>{visit.agentName}{visit.departmentName ? ` · ${visit.departmentName}` : ''}</Text>
        </View>
      )}
      <View style={styles.detailsRow}>
        <Text style={styles.detailsLabel}>{t('reports.calls.status')}</Text>
        <Text style={styles.detailsValue}>{t(`visits.status.${visit.status}` as any)}</Text>
      </View>
      <View style={styles.detailsRow}>
        <Text style={styles.detailsLabel}>{t('reports.visits.priority')}</Text>
        <Text style={styles.detailsValue}>{t(`visits.priority.${visit.priority}` as any)}</Text>
      </View>
      {!!visit.outcomeLabel && (
        <>
          <View style={styles.detailsPanelDivider} />
          <Text style={styles.detailsLabel}>{t('reports.visits.outcome')}</Text>
          <Text style={styles.detailsSecondaryLine}>{visit.outcomeLabel}{visit.outcomeResultType ? ` · ${visit.outcomeResultType}` : ''}</Text>
        </>
      )}
      <Text style={styles.detailsLabel}>{t('visits.timeline.title')}</Text>
      <VisitTiming visit={visit} timeline />
    </View>
  );
};

/** Right-pane detail for a single flat message row (the "All" combined timeline tab
 * — unlike Messages' contact-grouped view, each timeline row IS one individual
 * message, so this shows that one message directly rather than a contact summary. */
const FlatMessageDetailsPanel: React.FC<{ message: FlatMessageRow; onOpenConversation: () => void }> = ({ message, onOpenConversation }) => {
  const styles = useThemedStyles(getStyles);
  const { t, locale } = useLocale();
  return (
    <View style={styles.detailsPanel}>
      <View style={styles.detailsPanelHeader}>
        <ContactAvatar name={message.contactName} avatar={message.contactAvatar} channel={message.channel} size={48} />
        <Text style={styles.detailsPanelName} numberOfLines={1}>{message.contactName || message.contactPhone || message.contactId}</Text>
        {!!message.contactPhone && <Text style={styles.detailsPanelSub}>{message.contactPhone}</Text>}
        <TouchableOpacity style={styles.openConversationBtn} onPress={onOpenConversation}>
          <Ionicons name="chatbubble-ellipses-outline" size={14} color="#FFFFFF" />
          <Text style={styles.openConversationBtnText}>{t('reports.openConversation')}</Text>
        </TouchableOpacity>
      </View>
      <View style={styles.detailsPanelDivider} />
      <View style={styles.detailsRow}>
        <Text style={styles.detailsLabel}>{t('reports.filters.channel')}</Text>
        <View style={[styles.detailsChannelChip, { backgroundColor: getChannelColor(message.channel) + '20' }]}>
          <Ionicons name={getChannelIcon(message.channel) as any} size={12} color={getChannelColor(message.channel)} />
          <Text style={[styles.detailsChannelChipText, { color: getChannelColor(message.channel) }]}>{message.channel}</Text>
        </View>
      </View>
      {!!message.agentName && (
        <View style={styles.detailsRow}>
          <Text style={styles.detailsLabel}>{t('reports.filters.user')}</Text>
          <Text style={styles.detailsValue}>{message.agentName}{message.departmentName ? ` · ${message.departmentName}` : ''}</Text>
        </View>
      )}
      <View style={styles.detailsRow}>
        <Text style={styles.detailsLabel}>{t('reports.filters.startDate')}</Text>
        <Text style={styles.detailsValue}>{formatReportDateTime(message.timestamp, locale)}</Text>
      </View>
      <View style={styles.detailsPanelDivider} />
      <Text style={styles.detailsSecondaryLine}>{message.type} · {message.direction} · {message.status}</Text>
      <Text style={styles.detailsContent}>{message.content}</Text>
    </View>
  );
};

export const ReportsScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, setActiveTab, activeTab, handleConversationNavigation } = useAppContext();
  const { t, locale } = useLocale();
  const rtl = useRTLStyles();
  const { hasPermissionAtLeast, refreshPermissions } = usePermissions();
  const exportLang = locale?.startsWith('ar') ? 'ar' : 'en';

  // Reports permissions are read straight from the JWT (no round trip — see
  // usePermissions), which is only re-resolved server-side on login or token refresh.
  // A grant an admin just changed (e.g. bumping someone to View/Manager tier) wouldn't
  // show up here until the access token's natural ~1h expiry without this — forcing a
  // refresh on mount means visiting this screen always reflects the caller's CURRENT
  // permissions rather than whatever was true up to an hour ago.
  useEffect(() => {
    refreshPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canView = hasPermissionAtLeast('reports.view', 'view');
  const canExport = hasPermissionAtLeast('reports.export', 'edit');
  const canManageSaved = hasPermissionAtLeast('reports.manageSavedReports', 'view');
  const canEditSaved = hasPermissionAtLeast('reports.manageSavedReports', 'edit');

  const [showLeftPane, setShowLeftPane] = useState(Platform.OS === 'web');
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [selectedCallId, setSelectedCallId] = useState<string | null>(null);
  const [selectedVisitId, setSelectedVisitId] = useState<string | null>(null);
  const [selectedTimelineKey, setSelectedTimelineKey] = useState<string | null>(null);

  const [tab, setTab] = useState<ReportTab>('all');
  const [preset, setPreset] = useState<DatePreset>('today');
  const [customStart, setCustomStart] = useState<Date | null>(null);
  const [customEnd, setCustomEnd] = useState<Date | null>(null);
  const [actionType, setActionType] = useState<string>('');
  const [showTypePicker, setShowTypePicker] = useState(false);
  const [templateNameFilter, setTemplateNameFilter] = useState<string>('');
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [whatsappTemplates, setWhatsappTemplates] = useState<{ templateName: string }[]>([]);
  const [callStatusFilter, setCallStatusFilter] = useState<string>('');
  const [showCallStatusPicker, setShowCallStatusPicker] = useState(false);
  const [visitStatusFilter, setVisitStatusFilter] = useState<string>('');
  const [showVisitStatusPicker, setShowVisitStatusPicker] = useState(false);
  const [contactFilters, setContactFilters] = useState<ReportContactSelection[]>([]);
  const contactIdFilters = useMemo(() => contactFilters.map((c) => c.id), [contactFilters]);
  const [sortKey, setSortKey] = useState<SortKey>('time');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [showSortPicker, setShowSortPicker] = useState(false);
  const [userIdFilters, setUserIdFilters] = useState<string[]>([]);
  const [departmentFilters, setDepartmentFilters] = useState<string[]>([]);
  const [showUserPicker, setShowUserPicker] = useState(false);
  const [filterUsers, setFilterUsers] = useState<FilterUser[]>([]);
  const [filterDepartments, setFilterDepartments] = useState<{ departmentId: string; name: string }[]>([]);
  const [performance, setPerformance] = useState<ReportsPerformance | null>(null);
  // "Group by contact" per tab (Messages starts grouped, as before)
  const [groupedTabs, setGroupedTabs] = useState<Record<string, boolean>>({ all: false, messages: true, calls: false, visits: false });
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [flatMessageItems, setFlatMessageItems] = useState<TimelineItem[]>([]);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');

  const [contactGroups, setContactGroups] = useState<ContactMessageGroup[]>([]);
  const [calls, setCalls] = useState<CallRow[]>([]);
  const [visits, setVisits] = useState<VisitRow[]>([]);
  const [timelineItems, setTimelineItems] = useState<TimelineItem[]>([]);
  const [summary, setSummary] = useState<ScopedSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [showSavedReports, setShowSavedReports] = useState(false);
  const [savedReports, setSavedReports] = useState<any[]>([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);

  const dateRange = useMemo(() => {
    if (preset === 'custom') {
      return { startDate: customStart ?? undefined, endDate: customEnd ?? undefined };
    }
    return buildPresetRange(preset);
  }, [preset, customStart, customEnd]);

  // The user filter only makes sense once there's more than one visible agent to
  // choose from — a View-tier caller's list is always just themselves, so hide the
  // control entirely rather than showing a picker with exactly one disabled option.
  useEffect(() => {
    if (!canView) return;
    apiService.getReportsFilterUsers()
      .then((res) => setFilterUsers(res.data || []))
      .catch((err) => console.warn('[ReportsScreen] Failed to load filter users:', err));
    apiService.getReportsFilterDepartments()
      .then((res) => setFilterDepartments(res.data || []))
      .catch((err) => console.warn('[ReportsScreen] Failed to load filter departments:', err));
  }, [canView]);

  // Real business WhatsApp templates for the messages type/template filter — replaces
  // the old "type = template" bucket (every template send, indistinguishable from
  // each other) with the ability to filter down to one specific approved template.
  useEffect(() => {
    if (!canView) return;
    apiService.getWhatsAppCustomTemplates({ approvedOnly: true })
      .then((res) => {
        const raw: any[] = (res.data as any)?.templates || (Array.isArray(res.data) ? res.data : []) || [];
        setWhatsappTemplates(raw.map((tpl: any) => ({ templateName: tpl.templateName || tpl.name })).filter((t) => t.templateName));
      })
      .catch((err) => console.warn('[ReportsScreen] Failed to load WhatsApp templates:', err));
  }, [canView]);

  const loadData = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      if (tab === 'all') {
        // Flat, chronologically-merged messages + calls — never grouped-by-contact,
        // so this always shows every individual record (the "combined timeline"
        // requested to replace the impression that the grouped Messages view had
        // silently stopped early once every distinct contact got one row).
        // Settled independently (not Promise.all) — with .all, ONE endpoint failing
        // (a transient 500, a timeout, etc.) rejected the whole pair and silently
        // blanked the entire "All" tab even though the other endpoint's data had
        // already loaded successfully. Each half degrades on its own now: a failed
        // fetch just contributes zero rows of that kind instead of wiping both.
        const [messagesOutcome, callsOutcome, visitsOutcome] = await Promise.allSettled([
          apiService.getReportsMessages({
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            type: actionType || undefined,
            templateName: templateNameFilter || undefined,
            contactIds: contactIdFilters,
            userIds: userIdFilters,
            departmentIds: departmentFilters,
            flat: true,
          }),
          apiService.getReportsCalls({
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            contactIds: contactIdFilters,
            userIds: userIdFilters,
            departmentIds: departmentFilters,
            status: callStatusFilter || undefined,
          }),
          apiService.getReportsVisits({
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            contactIds: contactIdFilters,
            userIds: userIdFilters,
            departmentIds: departmentFilters,
            status: visitStatusFilter || undefined,
          }),
        ]);
        if (messagesOutcome.status === 'rejected') {
          console.warn('[ReportsScreen] Failed to load messages for All tab:', messagesOutcome.reason);
        }
        if (callsOutcome.status === 'rejected') {
          console.warn('[ReportsScreen] Failed to load calls for All tab:', callsOutcome.reason);
        }
        if (visitsOutcome.status === 'rejected') {
          console.warn('[ReportsScreen] Failed to load visits for All tab:', visitsOutcome.reason);
        }
        const messagesRes = messagesOutcome.status === 'fulfilled' ? messagesOutcome.value : { data: [] };
        const callsRes = callsOutcome.status === 'fulfilled' ? callsOutcome.value : { data: [] };
        const visitsRes = visitsOutcome.status === 'fulfilled' ? visitsOutcome.value : { data: [] };
        const messageItems: TimelineItem[] = ((messagesRes.data || []) as FlatMessageRow[]).map((m) => ({ kind: 'message', timestamp: m.timestamp, data: m }));
        const callItems: TimelineItem[] = ((callsRes.data || []) as CallRow[]).map((c) => ({ kind: 'call', timestamp: c.startedAt, data: c }));
        const visitItems: TimelineItem[] = ((visitsRes.data || []) as VisitRow[]).map((v) => ({ kind: 'visit', timestamp: visitPrimaryTime(v) || '', data: v }));
        const merged = [...messageItems, ...callItems, ...visitItems].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        setTimelineItems(merged);
        if (Platform.OS === 'web' && merged.length > 0) {
          const first = merged[0];
          setSelectedTimelineKey(first.kind === 'message' ? first.data.messageId : first.data._id);
        }
      } else if (tab === 'messages' && !groupedTabs.messages) {
        const res = await apiService.getReportsMessages({
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          type: actionType || undefined,
          templateName: templateNameFilter || undefined,
          contactIds: contactIdFilters,
          userIds: userIdFilters,
          departmentIds: departmentFilters,
          flat: true,
        });
        setFlatMessageItems(((res.data || []) as FlatMessageRow[]).map((m) => ({ kind: 'message' as const, timestamp: m.timestamp, data: m })));
      } else if (tab === 'messages') {
        const res = await apiService.getReportsMessages({
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          type: actionType || undefined,
          templateName: templateNameFilter || undefined,
          contactIds: contactIdFilters,
          userIds: userIdFilters,
          departmentIds: departmentFilters,
        });
        const rows = (res.data || []) as ContactMessageGroup[];
        setContactGroups(rows);
        if (Platform.OS === 'web' && rows.length > 0) setSelectedContactId(rows[0].contactId);
      } else if (tab === 'calls') {
        const res = await apiService.getReportsCalls({
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          contactIds: contactIdFilters,
          userIds: userIdFilters,
          departmentIds: departmentFilters,
          status: callStatusFilter || undefined,
        });
        const rows = (res.data || []) as CallRow[];
        setCalls(rows);
        if (Platform.OS === 'web' && rows.length > 0) setSelectedCallId(rows[0]._id);
      } else if (tab === 'performance') {
        // Loaded by loadPerformance below
      } else if (tab === 'visits') {
        const res = await apiService.getReportsVisits({
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          contactIds: contactIdFilters,
          userIds: userIdFilters,
          departmentIds: departmentFilters,
          status: visitStatusFilter || undefined,
        });
        const rows = (res.data || []) as VisitRow[];
        setVisits(rows);
        if (Platform.OS === 'web' && rows.length > 0) setSelectedVisitId(rows[0]._id);
      }
    } catch (err) {
      console.warn('[ReportsScreen] Failed to load report data:', err);
    } finally {
      setLoading(false);
    }
  }, [canView, tab, groupedTabs.messages, dateRange.startDate, dateRange.endDate, actionType, templateNameFilter, contactIdFilters, userIdFilters, departmentFilters, callStatusFilter, visitStatusFilter]);

  // Mirrors loadData's own filters exactly (contactIdFilter/userIdFilter) so the KPI
  // strip and charts always describe the SAME rows the table below is currently
  // showing — previously this only depended on date range, so the summary/charts
  // silently stayed unfiltered while the table narrowed further whenever a contact
  // or agent filter was applied.
  const loadSummary = useCallback(async () => {
    if (!canView) return;
    setSummaryLoading(true);
    try {
      const res = await apiService.getReportsSummary({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        contactIds: contactIdFilters,
        userIds: userIdFilters,
        departmentIds: departmentFilters,
        visitStatus: tab === 'visits' ? visitStatusFilter || undefined : undefined,
      });
      setSummary(res.data || null);
    } catch (err) {
      console.warn('[ReportsScreen] Failed to load report summary:', err);
    } finally {
      setSummaryLoading(false);
    }
  }, [canView, dateRange.startDate, dateRange.endDate, contactIdFilters, userIdFilters, departmentFilters, tab, visitStatusFilter]);

  // Sort-value extraction per row shape — 'name' and 'status' mean different fields
  // depending on which tab is active (e.g. a call has no message "content", a
  // timeline row can be either), so each tab resolves its own comparable values here
  // rather than forcing one shared shape.
  const timelineSortValue = (item: TimelineItem, key: SortKey): string | number => {
    if (key === 'time') return new Date(item.timestamp).getTime();
    if (key === 'name') {
      if (item.kind === 'message') return (item.data.contactName || item.data.contactPhone || item.data.contactId) || '';
      if (item.kind === 'call') return (item.data.contactName || item.data.phoneNumber) || '';
      return item.data.contactName || '';
    }
    if (item.kind === 'message') return item.data.type || '';
    return item.data.status || '';
  };
  const contactGroupSortValue = (group: ContactMessageGroup, key: SortKey): string | number => {
    if (key === 'time') return new Date(group.lastMessage.timestamp).getTime();
    if (key === 'name') return (group.contactName || group.contactPhone || group.contactId) || '';
    return group.lastMessage.status || '';
  };
  const callSortValue = (call: CallRow, key: SortKey): string | number => {
    if (key === 'time') return new Date(call.startedAt).getTime();
    if (key === 'name') return (call.contactName || call.phoneNumber) || '';
    return call.status || '';
  };

  const visitSortValue = (visit: VisitRow, key: SortKey): string | number => {
    if (key === 'time') return new Date(visitPrimaryTime(visit) || 0).getTime();
    if (key === 'name') return visit.contactName || '';
    return visit.status || '';
  };

  const compareValues = (a: string | number, b: string | number, direction: SortDirection): number => {
    let cmp: number;
    if (typeof a === 'number' && typeof b === 'number') {
      cmp = a - b;
    } else {
      cmp = String(a).localeCompare(String(b));
    }
    return direction === 'asc' ? cmp : -cmp;
  };

  const sortedTimelineItems = useMemo(() => {
    return [...timelineItems].sort((a, b) => compareValues(timelineSortValue(a, sortKey), timelineSortValue(b, sortKey), sortDirection));
  }, [timelineItems, sortKey, sortDirection]);

  const sortedContactGroups = useMemo(() => {
    return [...contactGroups].sort((a, b) => compareValues(contactGroupSortValue(a, sortKey), contactGroupSortValue(b, sortKey), sortDirection));
  }, [contactGroups, sortKey, sortDirection]);

  const sortedCalls = useMemo(() => {
    return [...calls].sort((a, b) => compareValues(callSortValue(a, sortKey), callSortValue(b, sortKey), sortDirection));
  }, [calls, sortKey, sortDirection]);

  const sortedVisits = useMemo(() => {
    return [...visits].sort((a, b) => compareValues(visitSortValue(a, sortKey), visitSortValue(b, sortKey), sortDirection));
  }, [visits, sortKey, sortDirection]);

  const isGrouped = tab !== 'performance' && !!groupedTabs[tab];
  // Customer groups for All / Calls / Visits (Messages keeps its server-side grouping)
  const contactGroupsForTab = useMemo<ReportContactGroup[]>(() => {
    if (!isGrouped || tab === 'messages') return [];
    const items: TimelineItem[] = tab === 'all'
      ? sortedTimelineItems
      : tab === 'calls'
        ? sortedCalls.map((c) => ({ kind: 'call' as const, timestamp: c.startedAt, data: c }))
        : sortedVisits.map((v) => ({ kind: 'visit' as const, timestamp: visitPrimaryTime(v) || '', data: v }));
    const groups = groupReportItemsByContact(items);
    const dir = sortDirection === 'asc' ? 1 : -1;
    return groups.sort((a, b) => {
      if (sortKey === 'name') return dir * a.name.localeCompare(b.name);
      if (sortKey === 'status') return dir * (a.items.length - b.items.length);
      return dir * (new Date(a.lastAt).getTime() - new Date(b.lastAt).getTime());
    });
  }, [isGrouped, tab, sortedTimelineItems, sortedCalls, sortedVisits, sortKey, sortDirection]);
  const sortedFlatMessageItems = useMemo(() => (
    [...flatMessageItems].sort((a, b) => compareValues(timelineSortValue(a, sortKey), timelineSortValue(b, sortKey), sortDirection))
  ), [flatMessageItems, sortKey, sortDirection]);
  // Whatever the list shows right now
  const listData: any[] = tab === 'performance'
    ? []
    : isGrouped && tab !== 'messages'
      ? contactGroupsForTab
      : tab === 'messages' && !isGrouped
        ? sortedFlatMessageItems
        : tab === 'all' ? sortedTimelineItems : tab === 'messages' ? sortedContactGroups : tab === 'calls' ? sortedCalls : sortedVisits;
  const toggleGroup = (key: string) => setExpandedGroups((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  // Row count for whichever tab is active — drives initialNumToRender/
  // maxToRenderPerBatch below so the FlatList renders everything in one pass
  // instead of relying on scroll-driven windowing (see the comment at the
  // FlatList itself for why that windowing never fires on this screen).
  // Math.max(1, ...) since FlatList treats 0 as "use the default of 10".
  const currentTabItemCount = Math.max(1, listData.length);

  // Export only applies to the Messages/Calls tabs — the combined "All" timeline has
  // no export endpoint of its own (it's a client-side merge of the other two), so the
  // export button hides on that tab rather than guessing which underlying dataset the
  // caller meant.
  // A single download covers all reports, so it's available on every tab
  const canExportCurrentTab = true;

  useEffect(() => {
    setActiveTab('performance');
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  // Serving-time / SLA figures — only fetched while the Performance tab is open
  // (it reads messages, calls, visits and assignments across the whole range).
  const loadPerformance = useCallback(async () => {
    if (!canView || tab !== 'performance') return;
    setPerformanceLoading(true);
    try {
      const res = await apiService.getReportsPerformance({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        contactIds: contactIdFilters,
        userIds: userIdFilters,
        departmentIds: departmentFilters,
      });
      setPerformance(res.data || null);
    } catch (err) {
      console.warn('[ReportsScreen] Failed to load performance report:', err);
    } finally {
      setPerformanceLoading(false);
    }
  }, [canView, tab, dateRange.startDate, dateRange.endDate, contactIdFilters, userIdFilters, departmentFilters]);

  useEffect(() => {
    loadPerformance();
  }, [loadPerformance]);

  const handleExport = async (format: 'csv' | 'xlsx' | 'pdf') => {
    setShowExportMenu(false);
    try {
      const exportParams = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        contactIds: contactIdFilters,
        userIds: userIdFilters,
        departmentIds: departmentFilters,
        lang: exportLang,
        // Exports print date + time in the viewer's own time zone
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };
      // filename comes from the server's Content-Disposition header (business name +
      // report type + date range + active filters — see buildExportFilename in
      // reports.controller.ts) rather than being invented client-side, which previously
      // silently discarded the server's friendly name in favor of a generic
      // "messages-report.csv" every time regardless of business or filters.
      // One download for every report (performance, messages, calls, visits)
      // with all the current filters, whichever tab is open.
      const { blob, filename } = await apiService.exportReportsAll(format, {
        ...exportParams,
        type: actionType || undefined,
        templateName: templateNameFilter || undefined,
        callStatus: callStatusFilter || undefined,
        visitStatus: visitStatusFilter || undefined,
      });
      if (Platform.OS === 'web' && typeof document !== 'undefined') {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.warn('[ReportsScreen] Export failed:', err);
    }
  };

  const loadSavedReports = useCallback(async () => {
    try {
      const res = await apiService.getSavedReports();
      setSavedReports(res.data || []);
    } catch (err) {
      console.warn('[ReportsScreen] Failed to load saved reports:', err);
    }
  }, []);

  const openSavedReports = () => {
    setShowSavedReports(true);
    loadSavedReports();
  };

  const applySavedReport = (report: any) => {
    const f = report.filters || {};
    if (f.preset) setPreset(f.preset);
    if (f.customStart) setCustomStart(new Date(f.customStart));
    if (f.customEnd) setCustomEnd(new Date(f.customEnd));
    setActionType(f.actionType || '');
    setTemplateNameFilter(f.templateName || '');
    setCallStatusFilter(f.callStatus || '');
    // Older saved reports stored a single contactId/userId
    setContactFilters(Array.isArray(f.contacts) ? f.contacts : f.contactId ? [{ id: f.contactId, label: f.contactLabel }] : []);
    setUserIdFilters(Array.isArray(f.userIds) ? f.userIds : f.userId ? [f.userId] : []);
    setDepartmentFilters(Array.isArray(f.departmentIds) ? f.departmentIds : []);
    if (f.tab) setTab(f.tab);
    setShowSavedReports(false);
  };

  const handleSaveReport = async () => {
    if (!saveName.trim()) return;
    try {
      await apiService.createSavedReport({
        name: saveName.trim(),
        reportType: tab,
        filters: {
          preset,
          customStart: customStart?.toISOString(),
          customEnd: customEnd?.toISOString(),
          actionType,
          templateName: templateNameFilter,
          callStatus: callStatusFilter,
          contacts: contactFilters,
          userIds: userIdFilters,
          departmentIds: departmentFilters,
          tab,
        },
        isTemplate: saveAsTemplate,
      });
      setShowSaveDialog(false);
      setSaveName('');
      setSaveAsTemplate(false);
    } catch (err) {
      console.warn('[ReportsScreen] Failed to save report:', err);
    }
  };

  const handleDeleteSavedReport = async (id: string) => {
    try {
      await apiService.deleteSavedReport(id);
      loadSavedReports();
    } catch (err) {
      console.warn('[ReportsScreen] Failed to delete saved report:', err);
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const selectedGroup = contactGroups.find((g) => g.contactId === selectedContactId) || null;
  const selectedCall = calls.find((c) => c._id === selectedCallId) || null;
  const selectedVisit = visits.find((v) => v._id === selectedVisitId) || null;
  const selectedTimelineItem = timelineItems.find((item) => (item.kind === 'message' ? item.data.messageId : item.data._id) === selectedTimelineKey) || null;

  const typeDonutSegments = useMemo(() => {
    if (!summary?.typeBreakdown?.length) return [];
    const palette = ['#25D366', '#0EA5E9', '#F59E0B', '#8B5CF6', '#EF4444', '#10B981', '#6366F1', '#EC4899'];
    return summary.typeBreakdown.slice(0, 8).map((t, i) => ({ label: t.type, value: t.count, color: palette[i % palette.length] }));
  }, [summary]);

  const channelBarData = useMemo(() => {
    if (!summary?.channelBreakdown?.length) return [];
    return summary.channelBreakdown.map((c) => ({ label: c.channel, value: c.count }));
  }, [summary]);

  const visitOutcomeDonutSegments = useMemo(() => {
    if (!summary?.visits?.outcomeBreakdown?.length) return [];
    const palette = ['#25D366', '#0EA5E9', '#F59E0B', '#8B5CF6', '#EF4444', '#10B981', '#6366F1', '#EC4899'];
    return summary.visits.outcomeBreakdown.slice(0, 8).map((o, i) => ({ label: o.outcomeLabel, value: o.count, color: palette[i % palette.length] }));
  }, [summary]);

  const agentFilterCount = userIdFilters.length + departmentFilters.length;
  const selectedUserLabel = agentFilterCount === 0
    ? undefined
    : agentFilterCount === 1
      ? (userIdFilters.length
        ? filterUsers.find((u) => u.userId === userIdFilters[0])?.name
        : filterDepartments.find((d) => d.departmentId === departmentFilters[0])?.name)
      : t('reports.filters.agentsSelected', { count: agentFilterCount });
  const toggleUserFilter = (userId: string) =>
    setUserIdFilters((prev) => (prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]));
  const toggleDepartmentFilter = (departmentId: string) =>
    setDepartmentFilters((prev) => (prev.includes(departmentId) ? prev.filter((id) => id !== departmentId) : [...prev, departmentId]));

  const filteredUserOptions = useMemo(() => {
    const allOption: FilterUser = { userId: '', name: t('reports.filters.allUsers') };
    const query = userSearchQuery.trim().toLowerCase();
    const matches = !query
      ? filterUsers
      : filterUsers.filter((u) => u.name.toLowerCase().includes(query) || u.email?.toLowerCase().includes(query));
    return [allOption, ...matches];
  }, [filterUsers, userSearchQuery, t]);

  if (!canView) {
    return (
      <ScreenLayout
        header={<Header showNotifications={false} showMessages={false} showNavigation={Platform.OS === 'web'} showBackButton={Platform.OS !== 'web'} onBackPress={() => navigateTo('home')} activeTab={activeTab} onTabPress={handleTabPress} />}
        showBottomTabs={Platform.OS !== 'web'}
      >
        <View style={styles.centered}>
          <Ionicons name="lock-closed-outline" size={32} color={colors.text.disabled as string} />
          <Text style={styles.emptyText}>{t('reports.noAccess')}</Text>
        </View>
      </ScreenLayout>
    );
  }

  const emptyDetailsHint = (icon: string) => (
    <View style={styles.detailsEmpty}>
      <Ionicons name={icon as any} size={32} color={colors.text.disabled as string} />
      <Text style={styles.detailsEmptyText}>{t('reports.selectRowHint')}</Text>
    </View>
  );

  let rightPaneDetail: React.ReactNode;
  if (tab === 'all') {
    if (!selectedTimelineItem) {
      rightPaneDetail = emptyDetailsHint('time-outline');
    } else if (selectedTimelineItem.kind === 'message') {
      rightPaneDetail = <FlatMessageDetailsPanel message={selectedTimelineItem.data} onOpenConversation={() => handleConversationNavigation(selectedTimelineItem.data.conversationId)} />;
    } else if (selectedTimelineItem.kind === 'call') {
      rightPaneDetail = <CallDetailsPanel call={selectedTimelineItem.data} />;
    } else {
      rightPaneDetail = <VisitDetailsPanel visit={selectedTimelineItem.data} />;
    }
  } else if (tab === 'messages' && !isGrouped) {
    const selectedFlat = flatMessageItems.find((m) => m.kind === 'message' && m.data.messageId === selectedTimelineKey);
    rightPaneDetail = selectedFlat && selectedFlat.kind === 'message'
      ? <FlatMessageDetailsPanel message={selectedFlat.data} onOpenConversation={() => handleConversationNavigation(selectedFlat.data.conversationId)} />
      : emptyDetailsHint('document-text-outline');
  } else if (tab === 'messages') {
    rightPaneDetail = selectedGroup ? (
      <ContactGroupDetailsPanel group={selectedGroup} onOpenConversation={() => handleConversationNavigation(selectedGroup.conversationId)} />
    ) : emptyDetailsHint('document-text-outline');
  } else if (tab === 'calls') {
    // key forces a full unmount/remount of the whole subtree (CallDetailsPanel ->
    // CallRecordingCell -> VoiceNotePlayer) on every call selection — without it,
    // React reconciles CallDetailsPanel in place across different `call` props (same
    // component type/position), so VoiceNotePlayer's own Audio.Sound instance
    // (soundRef) survived a call switch and kept playing the PREVIOUS recording even
    // after useCallRecording reset its state, since only a true unmount runs
    // VoiceNotePlayer's cleanup effect that calls soundRef.current.unloadAsync().
    rightPaneDetail = selectedCall ? <CallDetailsPanel key={selectedCall._id} call={selectedCall} /> : emptyDetailsHint('call-outline');
  } else {
    rightPaneDetail = selectedVisit ? <VisitDetailsPanel key={selectedVisit._id} visit={selectedVisit} /> : emptyDetailsHint('location-outline');
  }

  return (
    <ScreenLayout
      header={<Header showNotifications={false} showMessages={false} showNavigation={Platform.OS === 'web'} showBackButton={Platform.OS !== 'web'} onBackPress={() => navigateTo('home')} activeTab={activeTab} onTabPress={handleTabPress} />}
      showBottomTabs={Platform.OS !== 'web'}
      showLeftPane={showLeftPane}
      onLeftPaneClose={() => setShowLeftPane(false)}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      rightPaneContent="custom"
      rightPaneCustomContent={rightPaneDetail}
    >
      <View style={styles.container}>
        <View style={[styles.titleRow, { flexDirection: rtl.rowDirection }]}>
          <Text style={[styles.title, { textAlign: rtl.textStart }]}>{t('reports.title')}</Text>
          <View style={[styles.headerActions, { flexDirection: rtl.rowDirection }]}>
            {canManageSaved && (
              <TouchableOpacity style={styles.headerButton} onPress={openSavedReports}>
                <Ionicons name="bookmark-outline" size={18} color={colors.primary as string} />
              </TouchableOpacity>
            )}
            {canEditSaved && (
              <TouchableOpacity style={styles.headerButton} onPress={() => setShowSaveDialog(true)}>
                <Ionicons name="save-outline" size={18} color={colors.primary as string} />
              </TouchableOpacity>
            )}
            {canExport && canExportCurrentTab && (
              <TouchableOpacity style={styles.headerButton} onPress={() => setShowExportMenu(true)}>
                <Ionicons name="download-outline" size={18} color={colors.primary as string} />
              </TouchableOpacity>
            )}
          </View>
        </View>

        {/* Filters — a single inline row (date range + contact + agent +
            type/template/status) sitting ABOVE the KPI strip, charts, and tabs, since
            these filters govern the whole screen (summary + charts + every tab), not
            just whatever tab happens to be active. Previously the date range sat
            between the charts and the tabs, and contact/agent/type sat below the
            tabs — visually implying they only affected the list beneath them, when
            in fact they always drove the KPI/chart data above too. */}
        <View style={[styles.filtersBar, { flexDirection: rtl.rowDirection }]}>
          <DateRangePicker
            preset={preset}
            startDate={preset === 'custom' ? (customStart ?? undefined) : undefined}
            endDate={preset === 'custom' ? (customEnd ?? undefined) : undefined}
            onChange={(p, start, end) => {
              setPreset(p);
              if (p === 'custom') {
                setCustomStart(start ?? null);
                setCustomEnd(end ?? null);
              }
            }}
          />
          <ReportContactPicker value={contactFilters} onChange={setContactFilters} />
          {/* Only shown once the caller has more than one visible agent to
              filter by — View tier's list is always just themselves. */}
          {(filterUsers.length > 1 || filterDepartments.length > 0) && (
            <TouchableOpacity style={[styles.typePickerBtn, { flexDirection: rtl.rowDirection }]} onPress={() => setShowUserPicker(true)}>
              <Ionicons name="person-outline" size={14} color={colors.text.secondary as string} />
              <Text style={styles.typePickerBtnText} numberOfLines={1}>{selectedUserLabel || t('reports.filters.allUsers')}</Text>
              <Ionicons name="chevron-down" size={14} color={colors.text.secondary as string} />
            </TouchableOpacity>
          )}
          {tab !== 'calls' && tab !== 'visits' && tab !== 'performance' && (
            <TouchableOpacity style={[styles.typePickerBtn, { flexDirection: rtl.rowDirection }]} onPress={() => setShowTypePicker(true)}>
              <Ionicons name="funnel-outline" size={14} color={colors.text.secondary as string} />
              <Text style={styles.typePickerBtnText} numberOfLines={1}>{actionType ? actionType : t('reports.filters.allTypes')}</Text>
              <Ionicons name="chevron-down" size={14} color={colors.text.secondary as string} />
            </TouchableOpacity>
          )}
          {tab !== 'calls' && tab !== 'visits' && tab !== 'performance' && (
            <TouchableOpacity style={[styles.typePickerBtn, { flexDirection: rtl.rowDirection }]} onPress={() => setShowTemplatePicker(true)}>
              <Ionicons name="document-text-outline" size={14} color={colors.text.secondary as string} />
              <Text style={styles.typePickerBtnText} numberOfLines={1}>{templateNameFilter || t('reports.filters.allTemplates')}</Text>
              <Ionicons name="chevron-down" size={14} color={colors.text.secondary as string} />
            </TouchableOpacity>
          )}
          {(tab === 'all' || tab === 'calls') && (
            <TouchableOpacity style={[styles.typePickerBtn, { flexDirection: rtl.rowDirection }]} onPress={() => setShowCallStatusPicker(true)}>
              <Ionicons name="options-outline" size={14} color={colors.text.secondary as string} />
              <Text style={styles.typePickerBtnText} numberOfLines={1}>{callStatusFilter || t('reports.filters.allStatuses')}</Text>
              <Ionicons name="chevron-down" size={14} color={colors.text.secondary as string} />
            </TouchableOpacity>
          )}
          {tab === 'visits' && (
            <TouchableOpacity style={[styles.typePickerBtn, { flexDirection: rtl.rowDirection }]} onPress={() => setShowVisitStatusPicker(true)}>
              <Ionicons name="options-outline" size={14} color={colors.text.secondary as string} />
              <Text style={styles.typePickerBtnText} numberOfLines={1}>{visitStatusFilter || t('reports.filters.allStatuses')}</Text>
              <Ionicons name="chevron-down" size={14} color={colors.text.secondary as string} />
            </TouchableOpacity>
          )}
          <TouchableOpacity style={[styles.typePickerBtn, { flexDirection: rtl.rowDirection }]} onPress={() => setShowSortPicker(true)}>
            <Ionicons name={sortDirection === 'asc' ? 'arrow-up-outline' : 'arrow-down-outline'} size={14} color={colors.text.secondary as string} />
            <Text style={styles.typePickerBtnText} numberOfLines={1}>{t(`reports.sort.${sortKey}`)}</Text>
            <Ionicons name="chevron-down" size={14} color={colors.text.secondary as string} />
          </TouchableOpacity>
        </View>

        <FlatList
          data={listData}
          keyExtractor={(item, index) => (
            isGrouped && tab !== 'messages' ? item.key
              : tab === 'all' || (tab === 'messages' && !isGrouped) ? `${item.kind}-${item.kind === 'message' ? item.data.messageId : item.data._id}`
                : tab === 'messages' ? item.contactId : item._id
          ) || String(index)}
          contentContainerStyle={styles.list}
          // This screen scrolls at the document/<body> level (this app's whole web
          // layout model — see web/index.html's `body { overflow-y: auto }`, no
          // bounded-height ancestor for the FlatList to own its own scroll region
          // inside). FlatList's virtualization depends on receiving scroll events on
          // ITS OWN scrollable node to decide when to mount more rows past the
          // initial batch — since the page, not the FlatList, is what actually
          // scrolls here, those events never fire and rows past
          // initialNumToRender never mount (a permanently blank reserved space
          // below the initial batch, confirmed via inspecting the rendered DOM: a
          // correctly-sized empty spacer div sat below exactly 10 rendered rows
          // and never filled in on scroll). Rendering the full list up front
          // sidesteps the broken windowing entirely — safe here since Reports
          // datasets are bounded/paginated business data, not an unbounded feed.
          initialNumToRender={currentTabItemCount}
          maxToRenderPerBatch={currentTabItemCount}
          ListHeaderComponent={
            <View>
              {/* KPI strip — serving-time cards on the Performance tab */}
              {tab === 'performance' ? (
                performance ? (() => {
                  const p = performance.totals;
                  const units = { s: t('reports.duration.s'), m: t('reports.duration.m'), h: t('reports.duration.h'), d: t('reports.duration.d') };
                  const d = (v?: number | null) => formatReportDuration(v, units, { compact: true });
                  const pct = (a: number, b: number) => (b > 0 ? `${Math.round((a / b) * 100)}%` : '—');
                  return (
                    <View style={[styles.kpiRow, { flexDirection: rtl.rowDirection }]}>
                      <KpiTile minWidth={150} labelLines={2} icon="chatbubbles-outline" label={t('reports.kpi.messagesTotal')} value={p.activity.messagesTotal} index={0} />
                      <KpiTile minWidth={150} labelLines={2} icon="time-outline" label={t('reports.kpi.callTimeTotal')} value={d(p.activity.callTimeTotalSeconds || 0)} index={1} />
                      <KpiTile minWidth={150} labelLines={2} icon="people-outline" label={t('reports.kpi.customersReached')} value={p.activity.customersReached} index={2} />
                      <KpiTile minWidth={150} labelLines={2} icon="chatbubble-ellipses-outline" label={t('reports.kpi.avgResponse')} value={d(p.messages.responseTime?.avgSeconds)} index={3} />
                      <KpiTile minWidth={150} labelLines={2} icon="call-outline" label={t('reports.kpi.answerRate')} value={pct(p.calls.answered, p.calls.total)} index={4} />
                      <KpiTile minWidth={150} labelLines={2} icon="checkmark-done-outline" label={t('reports.kpi.visitCompletion')} value={pct(p.visits.completed, p.visits.total)} index={5} />
                    </View>
                  );
                })() : null
              ) : summaryLoading ? (
                <View style={styles.kpiLoading}>
                  <ActivityIndicator color={colors.primary as string} />
                </View>
              ) : summary && tab !== 'visits' ? (
                <View style={[styles.kpiRow, { flexDirection: rtl.rowDirection }]}>
                  <KpiTile minWidth={150} labelLines={2} icon="chatbubbles-outline" label={t('reports.kpi.messages')} value={summary.messages.total} index={0} />
                  <KpiTile minWidth={150} labelLines={2} icon="arrow-down-circle-outline" label={t('reports.kpi.inbound')} value={summary.messages.inbound} index={1} />
                  <KpiTile minWidth={150} labelLines={2} icon="arrow-up-circle-outline" label={t('reports.kpi.outbound')} value={summary.messages.outbound} index={2} />
                  <KpiTile minWidth={150} labelLines={2} icon="call-outline" label={t('reports.kpi.calls')} value={summary.calls.total} index={3} />
                  <KpiTile minWidth={150} labelLines={2} icon="checkmark-circle-outline" label={t('reports.kpi.completedCalls')} value={summary.calls.completed} index={4} />
                  <KpiTile minWidth={150} labelLines={2} icon="location-outline" label={t('reports.kpi.visits')} value={summary.visits.total} index={5} />
                  <KpiTile minWidth={150} labelLines={2} icon="checkmark-done-outline" label={t('reports.kpi.completedVisits')} value={summary.visits.completed} index={6} />
                </View>
              ) : null}

              {tab === 'visits' && summary?.visits.analytics && <VisitAnalyticsPanel analytics={summary.visits.analytics} byAgent={summary.visits.byAgent || []} statuses={summary.visits.statusBreakdown || []} onAgentPress={(id) => setUserIdFilters([id])} />}

              {/* Charts */}
              {tab !== 'performance' && summary && (summary.messages.dailyVolume.length > 0 || summary.calls.dailyVolume.length > 0 || summary.visits.dailyVolume.length > 0) && (
                <AreaChartCard
                  title={tab === 'calls' ? t('reports.charts.callVolume') : tab === 'visits' ? t('reports.charts.visitVolume') : t('reports.charts.messageVolume')}
                  data={tab === 'calls' ? summary.calls.dailyVolume : tab === 'visits' ? summary.visits.dailyVolume : summary.messages.dailyVolume}
                  height={160}
                />
              )}

              {(tab === 'all' || tab === 'messages') && (typeDonutSegments.length > 0 || channelBarData.length > 0) && (
                <View style={[styles.chartsRow, { flexDirection: rtl.rowDirection }]}>
                  {typeDonutSegments.length > 0 && (
                    <View style={styles.chartCard}>
                      <Text style={styles.chartCardTitle}>{t('reports.charts.typeBreakdown')}</Text>
                      <DonutChart segments={typeDonutSegments} size={140} centerLabel={String(summary?.messages.total ?? '')} />
                    </View>
                  )}
                  {channelBarData.length > 0 && (
                    <View style={styles.chartCard}>
                      <BarChartCard title={t('reports.charts.channelBreakdown')} data={channelBarData} height={140} />
                    </View>
                  )}
                </View>
              )}

              {tab === 'visits' && visitOutcomeDonutSegments.length > 0 && (
                <View style={[styles.chartsRow, { flexDirection: rtl.rowDirection }]}>
                  <View style={styles.chartCard}>
                    <Text style={styles.chartCardTitle}>{t('reports.charts.outcomeBreakdown')}</Text>
                    <DonutChart segments={visitOutcomeDonutSegments} size={140} centerLabel={String(summary?.visits.total ?? '')} />
                  </View>
                </View>
              )}

              {/* Tabs — "All" (a flat, never-grouped, chronologically-merged
                  messages+calls timeline) shown first, so a caller always has one
                  view guaranteed to show every individual record rather than only
                  ever seeing Messages' contact-grouped summary rows. */}
              <View style={[styles.tabRow, { flexDirection: rtl.rowDirection }]}>
                <TouchableOpacity style={[styles.tabBtn, tab === 'all' && styles.tabBtnActive]} onPress={() => setTab('all')}>
                  <Ionicons name="time-outline" size={14} color={tab === 'all' ? colors.primary as string : colors.text.secondary as string} />
                  <Text style={[styles.tabBtnText, tab === 'all' && styles.tabBtnTextActive]}>{t('reports.tabs.all')}</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[styles.tabBtn, tab === 'messages' && styles.tabBtnActive]} onPress={() => setTab('messages')}>
                  <Ionicons name="chatbubbles-outline" size={14} color={tab === 'messages' ? colors.primary as string : colors.text.secondary as string} />
                  <Text style={[styles.tabBtnText, tab === 'messages' && styles.tabBtnTextActive]}>{t('reports.tabs.messages')}</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[styles.tabBtn, tab === 'calls' && styles.tabBtnActive]} onPress={() => setTab('calls')}>
                  <Ionicons name="call-outline" size={14} color={tab === 'calls' ? colors.primary as string : colors.text.secondary as string} />
                  <Text style={[styles.tabBtnText, tab === 'calls' && styles.tabBtnTextActive]}>{t('reports.tabs.calls')}</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[styles.tabBtn, tab === 'visits' && styles.tabBtnActive]} onPress={() => setTab('visits')}>
                  <Ionicons name="location-outline" size={14} color={tab === 'visits' ? colors.primary as string : colors.text.secondary as string} />
                  <Text style={[styles.tabBtnText, tab === 'visits' && styles.tabBtnTextActive]}>{t('reports.tabs.visits')}</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[styles.tabBtn, tab === 'performance' && styles.tabBtnActive]} onPress={() => setTab('performance')}>
                  <Ionicons name="speedometer-outline" size={14} color={tab === 'performance' ? colors.primary as string : colors.text.secondary as string} />
                  <Text style={[styles.tabBtnText, tab === 'performance' && styles.tabBtnTextActive]}>{t('reports.tabs.performance')}</Text>
                </TouchableOpacity>
                {tab !== 'performance' && (
                  <TouchableOpacity
                    style={[styles.tabBtn, styles.groupToggle, isGrouped && styles.tabBtnActive]}
                    onPress={() => setGroupedTabs((prev) => ({ ...prev, [tab]: !prev[tab] }))}
                  >
                    <Ionicons name={isGrouped ? 'checkbox' : 'square-outline'} size={14} color={isGrouped ? colors.primary as string : colors.text.secondary as string} />
                    <Text style={[styles.tabBtnText, isGrouped && styles.tabBtnTextActive]}>{t('reports.groupByContact')}</Text>
                  </TouchableOpacity>
                )}
              </View>

              {tab === 'performance' && <ReportsPerformancePanel data={performance} loading={performanceLoading} />}

              {loading && (
                <View style={styles.centered}>
                  <ActivityIndicator color={colors.primary as string} />
                </View>
              )}
              {!loading && tab !== 'performance' && listData.length === 0 && (
                <View style={styles.centered}>
                  <Text style={styles.emptyText}>{t('reports.noData')}</Text>
                </View>
              )}
            </View>
          }
          renderItem={({ item, index }) => {
            if (isGrouped && tab !== 'messages') {
              const group = item as ReportContactGroup;
              return <ReportContactGroupRow group={group} index={index} expanded={expandedGroups.has(group.key)} onToggle={() => toggleGroup(group.key)} />;
            }
            if (tab === 'all' || (tab === 'messages' && !isGrouped)) {
              const timelineItem = item as TimelineItem;
              if (timelineItem.kind === 'message') {
                const m = timelineItem.data;
                const isSelected = m.messageId === selectedTimelineKey;
                return (
                  <TouchableOpacity
                    style={[styles.row, isSelected && styles.rowSelected, { flexDirection: rtl.rowDirection }]}
                    onPress={() => setSelectedTimelineKey(m.messageId)}
                    activeOpacity={0.7}
                  >
                    <IndexBadge index={index} />
                    <ContactAvatar name={m.contactName} avatar={m.contactAvatar} channel={m.channel} />
                    <View style={styles.rowInfo}>
                      <Text style={styles.rowPrimary} numberOfLines={1}>{m.contactName || m.contactPhone || m.contactId}</Text>
                      <Text style={styles.rowSecondary} numberOfLines={1}>{m.content}</Text>
                      <ReportBreakLine seconds={m.breakBeforeSeconds} />
                      {!!m.agentName && (
                        <View style={[styles.rowAgentLine, { flexDirection: rtl.rowDirection }]}>
                          <Ionicons name="person-outline" size={11} color={colors.text.disabled as string} />
                          <Text style={styles.rowAgentText} numberOfLines={1}>{m.agentName}{m.departmentName ? ` · ${m.departmentName}` : ''}</Text>
                        </View>
                      )}
                    </View>
                    <View style={styles.rowTrailing}>
                      <Text style={styles.rowTime}>{formatReportDateTime(m.timestamp, locale)}</Text>
                      <TouchableOpacity
                        style={styles.openConversationIconBtn}
                        onPress={() => handleConversationNavigation(m.conversationId)}
                        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                      >
                        <Ionicons name="open-outline" size={16} color={colors.primary as string} />
                      </TouchableOpacity>
                    </View>
                  </TouchableOpacity>
                );
              }
              if (timelineItem.kind === 'call') {
                const c = timelineItem.data;
                const isSelected = c._id === selectedTimelineKey;
                return (
                  <TouchableOpacity
                    style={[styles.row, isSelected && styles.rowSelected, { flexDirection: rtl.rowDirection }]}
                    onPress={() => setSelectedTimelineKey(c._id)}
                    activeOpacity={0.7}
                  >
                    <IndexBadge index={index} />
                    <ContactAvatar name={c.contactName} channel="phone" />
                    <View style={styles.rowInfo}>
                      <Text style={styles.rowPrimary} numberOfLines={1}>{c.contactName ? `${c.contactName} · ${c.phoneNumber}` : c.phoneNumber}</Text>
                      <Text style={styles.rowSecondary} numberOfLines={1}>{c.direction} · {c.status} · {c.duration ?? 0}s</Text>
                      <ReportBreakLine seconds={c.breakBeforeSeconds} />
                    </View>
                    <Text style={styles.rowTime}>{formatReportDateTime(c.startedAt, locale)}{c.endedAt ? ` → ${formatReportTime(c.endedAt, locale)}` : ''}</Text>
                  </TouchableOpacity>
                );
              }
              const v = timelineItem.data;
              const isVisitSelected = v._id === selectedTimelineKey;
              return (
                <TouchableOpacity
                  style={[styles.row, isVisitSelected && styles.rowSelected, { flexDirection: rtl.rowDirection }]}
                  onPress={() => setSelectedTimelineKey(v._id)}
                  activeOpacity={0.7}
                >
                  <IndexBadge index={index} />
                  <ContactAvatar name={v.contactName} channel="phone" />
                  <View style={styles.rowInfo}>
                    <Text style={styles.rowPrimary} numberOfLines={1}>{v.contactName || v.contactId}</Text>
                    <Text style={styles.rowSecondary} numberOfLines={1}>{v.campaignName ? `${v.campaignName} · ` : ''}{v.status} · {v.priority}</Text>
                    {!!v.outcomeLabel && <Text style={styles.rowSecondary} numberOfLines={1}>{v.outcomeLabel}</Text>}
                  </View>
                  <Text style={styles.rowTime}>{formatReportDateTime(visitPrimaryTime(v), locale)}</Text>
                </TouchableOpacity>
              );
            }
            if (tab === 'messages') {
              const group = item as ContactMessageGroup;
              const isSelected = group.contactId === selectedContactId;
              return (
                <TouchableOpacity
                  style={[styles.row, isSelected && styles.rowSelected, { flexDirection: rtl.rowDirection }]}
                  onPress={() => setSelectedContactId(group.contactId)}
                  activeOpacity={0.7}
                >
                  <IndexBadge index={index} />
                  <ContactAvatar name={group.contactName} avatar={group.contactAvatar} channel={group.channel} />
                  <View style={styles.rowInfo}>
                    <View style={[styles.rowPrimaryLine, { flexDirection: rtl.rowDirection }]}>
                      <Text style={styles.rowPrimary} numberOfLines={1}>{group.contactName || group.contactPhone || group.contactId}</Text>
                      {group.messageCount > 1 && (
                        <View style={styles.countBadge}>
                          <Text style={styles.countBadgeText}>{group.messageCount}</Text>
                        </View>
                      )}
                    </View>
                    <Text style={styles.rowSecondary} numberOfLines={1}>{group.lastMessage.content}</Text>
                    {!!group.lastMessage.agentName && (
                      <View style={[styles.rowAgentLine, { flexDirection: rtl.rowDirection }]}>
                        <Ionicons name="person-outline" size={11} color={colors.text.disabled as string} />
                        <Text style={styles.rowAgentText} numberOfLines={1}>
                          {group.lastMessage.agentName}{group.lastMessage.departmentName ? ` · ${group.lastMessage.departmentName}` : ''}
                        </Text>
                      </View>
                    )}
                  </View>
                  <View style={styles.rowTrailing}>
                    <Text style={styles.rowTime}>{formatReportDateTime(group.lastMessage.timestamp, locale)}</Text>
                    <TouchableOpacity
                      style={styles.openConversationIconBtn}
                      onPress={() => handleConversationNavigation(group.conversationId)}
                      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                    >
                      <Ionicons name="open-outline" size={16} color={colors.primary as string} />
                    </TouchableOpacity>
                  </View>
                </TouchableOpacity>
              );
            }
            if (tab === 'calls') {
              const row = item as CallRow;
              const isSelected = row._id === selectedCallId;
              return (
                <TouchableOpacity
                  style={[styles.row, isSelected && styles.rowSelected, { flexDirection: rtl.rowDirection }]}
                  onPress={() => setSelectedCallId(row._id)}
                  activeOpacity={0.7}
                >
                  <IndexBadge index={index} />
                  <ContactAvatar name={row.contactName} channel="phone" />
                  <View style={styles.rowInfo}>
                    <Text style={styles.rowPrimary} numberOfLines={1}>{row.contactName ? `${row.contactName} · ${row.phoneNumber}` : row.phoneNumber}</Text>
                    <Text style={styles.rowSecondary} numberOfLines={1}>{row.direction} · {row.status} · {row.duration ?? 0}s</Text>
                    <Text style={styles.rowSecondary}>{formatReportDateTime(row.startedAt, locale)}{row.endedAt ? ` → ${formatReportTime(row.endedAt, locale)}` : ''}</Text>
                    <ReportBreakLine seconds={row.breakBeforeSeconds} />
                    {!!row.agentName && (
                      <View style={[styles.rowAgentLine, { flexDirection: rtl.rowDirection }]}>
                        <Ionicons name="person-outline" size={11} color={colors.text.disabled as string} />
                        <Text style={styles.rowAgentText} numberOfLines={1}>
                          {row.agentName}{row.departmentName ? ` · ${row.departmentName}` : ''}
                        </Text>
                      </View>
                    )}
                  </View>
                  <CallRecordingCell call={row} />
                </TouchableOpacity>
              );
            }
            const visitRow = item as VisitRow;
            const isVisitRowSelected = visitRow._id === selectedVisitId;
            return (
              <TouchableOpacity
                style={[styles.row, isVisitRowSelected && styles.rowSelected, { flexDirection: rtl.rowDirection }]}
                onPress={() => setSelectedVisitId(visitRow._id)}
                activeOpacity={0.7}
              >
                <IndexBadge index={index} />
                <ContactAvatar name={visitRow.contactName} channel="phone" />
                <View style={styles.rowInfo}>
                  <Text style={styles.rowPrimary} numberOfLines={1}>{visitRow.contactName || visitRow.contactId}</Text>
                  <Text style={styles.rowSecondary} numberOfLines={1}>{visitRow.campaignName ? `${visitRow.campaignName} · ` : ''}{visitRow.status} · {visitRow.priority}</Text>
                  {!!visitRow.agentName && (
                    <View style={[styles.rowAgentLine, { flexDirection: rtl.rowDirection }]}>
                      <Ionicons name="person-outline" size={11} color={colors.text.disabled as string} />
                      <Text style={styles.rowAgentText} numberOfLines={1}>
                        {visitRow.agentName}{visitRow.departmentName ? ` · ${visitRow.departmentName}` : ''}
                      </Text>
                    </View>
                  )}
                </View>
                <Text style={styles.rowTime}>{formatReportDateTime(visitPrimaryTime(visitRow), locale)}</Text>
              </TouchableOpacity>
            );
          }}
        />
      </View>

      {/* Export format picker — a real Modal (portal-mounted at the document root on
          web via react-native-web), NOT an inline absolute-positioned dropdown. The
          previous inline dropdown kept rendering underneath other fixed-position
          UI (the ScreenLayout web header, panes, etc.) no matter how high its
          z-index was set, because it shared a stacking context with its siblings
          in this screen's own tree instead of escaping to the top level the way
          every other picker/dialog on this screen already does (see
          showTypePicker/showSavedReports/showSaveDialog below, which never had
          this problem for the same reason). */}
      <Modal visible={showExportMenu} transparent animationType="fade" onRequestClose={() => setShowExportMenu(false)}>
        <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={() => setShowExportMenu(false)}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>{t('reports.export.title')}</Text>
            <TouchableOpacity style={styles.typeOption} onPress={() => handleExport('csv')}>
              <Ionicons name="document-text-outline" size={18} color={colors.primary as string} />
              <Text style={styles.typeOptionText}>{t('reports.export.csv')}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.typeOption} onPress={() => handleExport('xlsx')}>
              <Ionicons name="grid-outline" size={18} color={colors.primary as string} />
              <Text style={styles.typeOptionText}>{t('reports.export.excel')}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.typeOption} onPress={() => handleExport('pdf')}>
              <Ionicons name="document-outline" size={18} color={colors.primary as string} />
              <Text style={styles.typeOptionText}>{t('reports.export.pdf')}</Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Action-type picker modal — FlatList (not a mapped View) so a long option
          list scrolls WITHIN the card's bounded height instead of overflowing past
          it, which is what a plain View + .map() here previously did. */}
      <Modal visible={showTypePicker} transparent animationType="fade" onRequestClose={() => setShowTypePicker(false)}>
        <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={() => setShowTypePicker(false)}>
          <TouchableOpacity activeOpacity={1} style={styles.modalCard} onPress={() => {}}>
            <Text style={styles.modalTitle}>{t('reports.filters.actionType')}</Text>
            <FlatList
              data={['', ...MESSAGE_TYPES]}
              keyExtractor={(mt) => mt || '__all__'}
              style={styles.modalList}
              keyboardShouldPersistTaps="handled"
              renderItem={({ item: mt }) => (
                <TouchableOpacity style={styles.typeOption} onPress={() => { setActionType(mt); setShowTypePicker(false); }}>
                  <Ionicons name={actionType === mt ? 'radio-button-on' : 'radio-button-off'} size={18} color={colors.primary as string} />
                  <Text style={styles.typeOptionText}>{mt || t('reports.filters.allTypes')}</Text>
                </TouchableOpacity>
              )}
            />
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

      {/* WhatsApp template filter picker — real approved business templates (from
          apiService.getWhatsAppCustomTemplates), not the generic type: 'template'
          bucket which lumps every template send together regardless of which
          template was actually used. */}
      <Modal visible={showTemplatePicker} transparent animationType="fade" onRequestClose={() => setShowTemplatePicker(false)}>
        <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={() => setShowTemplatePicker(false)}>
          <TouchableOpacity activeOpacity={1} style={styles.modalCard} onPress={() => {}}>
            <Text style={styles.modalTitle}>{t('reports.filters.template')}</Text>
            <FlatList
              data={['', ...whatsappTemplates.map((tpl) => tpl.templateName)]}
              keyExtractor={(name) => name || '__all__'}
              style={styles.modalList}
              keyboardShouldPersistTaps="handled"
              ListEmptyComponent={<Text style={styles.emptyText}>{t('reports.filters.noContactsFound')}</Text>}
              renderItem={({ item: name }) => (
                <TouchableOpacity style={styles.typeOption} onPress={() => { setTemplateNameFilter(name); setShowTemplatePicker(false); }}>
                  <Ionicons name={templateNameFilter === name ? 'radio-button-on' : 'radio-button-off'} size={18} color={colors.primary as string} />
                  <Text style={styles.typeOptionText}>{name || t('reports.filters.allTemplates')}</Text>
                </TouchableOpacity>
              )}
            />
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

      {/* Sort picker — a single "sort by" list (not per-column headers, per product
          decision to keep the existing card rows rather than rewrite into a literal
          grid table) with a direction toggle. Selecting the already-active key just
          flips its direction instead of doing nothing, so this doubles as the
          asc/desc control without a second row of buttons. */}
      <Modal visible={showSortPicker} transparent animationType="fade" onRequestClose={() => setShowSortPicker(false)}>
        <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={() => setShowSortPicker(false)}>
          <TouchableOpacity activeOpacity={1} style={styles.modalCard} onPress={() => {}}>
            <Text style={styles.modalTitle}>{t('reports.sort.title')}</Text>
            <FlatList
              data={SORT_KEYS}
              keyExtractor={(key) => key}
              style={styles.modalList}
              keyboardShouldPersistTaps="handled"
              renderItem={({ item: key }) => (
                <TouchableOpacity
                  style={[styles.typeOption, { flexDirection: rtl.rowDirection, justifyContent: 'space-between' }]}
                  onPress={() => {
                    if (sortKey === key) {
                      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
                    } else {
                      setSortKey(key);
                      setSortDirection(key === 'time' ? 'desc' : 'asc');
                    }
                    setShowSortPicker(false);
                  }}
                >
                  <View style={[styles.rowAgentLine, { flexDirection: rtl.rowDirection }]}>
                    <Ionicons name={sortKey === key ? 'radio-button-on' : 'radio-button-off'} size={18} color={colors.primary as string} />
                    <Text style={styles.typeOptionText}>{t(`reports.sort.${key}`)}</Text>
                  </View>
                  {sortKey === key && (
                    <Ionicons name={sortDirection === 'asc' ? 'arrow-up-outline' : 'arrow-down-outline'} size={16} color={colors.primary as string} />
                  )}
                </TouchableOpacity>
              )}
            />
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

      {/* Call status filter picker — PhoneCallModel.status's full enum. */}
      <Modal visible={showCallStatusPicker} transparent animationType="fade" onRequestClose={() => setShowCallStatusPicker(false)}>
        <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={() => setShowCallStatusPicker(false)}>
          <TouchableOpacity activeOpacity={1} style={styles.modalCard} onPress={() => {}}>
            <Text style={styles.modalTitle}>{t('reports.filters.callStatus')}</Text>
            <FlatList
              data={['', ...CALL_STATUSES]}
              keyExtractor={(s) => s || '__all__'}
              style={styles.modalList}
              keyboardShouldPersistTaps="handled"
              renderItem={({ item: s }) => (
                <TouchableOpacity style={styles.typeOption} onPress={() => { setCallStatusFilter(s); setShowCallStatusPicker(false); }}>
                  <Ionicons name={callStatusFilter === s ? 'radio-button-on' : 'radio-button-off'} size={18} color={colors.primary as string} />
                  <Text style={styles.typeOptionText}>{s || t('reports.filters.allStatuses')}</Text>
                </TouchableOpacity>
              )}
            />
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

      {/* Visit status filter picker — Visit.status's full enum. */}
      <Modal visible={showVisitStatusPicker} transparent animationType="fade" onRequestClose={() => setShowVisitStatusPicker(false)}>
        <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={() => setShowVisitStatusPicker(false)}>
          <TouchableOpacity activeOpacity={1} style={styles.modalCard} onPress={() => {}}>
            <Text style={styles.modalTitle}>{t('reports.filters.visitStatus')}</Text>
            <FlatList
              data={['', ...VISIT_STATUSES]}
              keyExtractor={(s) => s || '__all__'}
              style={styles.modalList}
              keyboardShouldPersistTaps="handled"
              renderItem={({ item: s }) => (
                <TouchableOpacity style={styles.typeOption} onPress={() => { setVisitStatusFilter(s); setShowVisitStatusPicker(false); }}>
                  <Ionicons name={visitStatusFilter === s ? 'radio-button-on' : 'radio-button-off'} size={18} color={colors.primary as string} />
                  <Text style={styles.typeOptionText}>{s || t('reports.filters.allStatuses')}</Text>
                </TouchableOpacity>
              )}
            />
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

      {/* User (agent) filter picker modal — options are exactly filterUsers, already
          server-resolved to what the caller's reports.view tier permits. Searchable
          (filters client-side by name/email) and scrollable via FlatList, since a
          Manager/Admin's visible-agent list can be long. */}
      <Modal visible={showUserPicker} transparent animationType="fade" onRequestClose={() => setShowUserPicker(false)}>
        <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={() => setShowUserPicker(false)}>
          <TouchableOpacity activeOpacity={1} style={styles.modalCard} onPress={() => {}}>
            <Text style={styles.modalTitle}>{t('reports.filters.user')}</Text>
            <TextInput
              style={styles.searchInput}
              placeholder={t('reports.filters.searchUsers')}
              placeholderTextColor={colors.text.disabled as string}
              value={userSearchQuery}
              onChangeText={setUserSearchQuery}
              autoCapitalize="none"
            />
            <FlatList
              data={filteredUserOptions}
              keyExtractor={(u) => u.userId || '__all__'}
              style={styles.modalList}
              keyboardShouldPersistTaps="handled"
              ListHeaderComponent={filterDepartments.length > 0 && !userSearchQuery.trim() ? (
                <View>
                  <Text style={styles.pickerSectionTitle}>{t('reports.filters.departments')}</Text>
                  {filterDepartments.map((d) => (
                    <TouchableOpacity key={d.departmentId} style={styles.typeOption} onPress={() => toggleDepartmentFilter(d.departmentId)}>
                      <Ionicons name={departmentFilters.includes(d.departmentId) ? 'checkbox' : 'square-outline'} size={18} color={colors.primary as string} />
                      <View style={styles.userOptionText}>
                        <Text style={styles.typeOptionText} numberOfLines={1}>{d.name}</Text>
                      </View>
                    </TouchableOpacity>
                  ))}
                  <Text style={styles.pickerSectionTitle}>{t('reports.filters.agents')}</Text>
                </View>
              ) : null}
              ListEmptyComponent={<Text style={styles.emptyText}>{t('reports.filters.noUsersFound')}</Text>}
              renderItem={({ item: u }) => {
                const isAll = !u.userId;
                const checked = isAll ? agentFilterCount === 0 : userIdFilters.includes(u.userId);
                return (
                  <TouchableOpacity
                    style={styles.typeOption}
                    onPress={() => {
                      if (isAll) {
                        setUserIdFilters([]);
                        setDepartmentFilters([]);
                      } else {
                        toggleUserFilter(u.userId);
                      }
                    }}
                  >
                    <Ionicons name={checked ? 'checkbox' : 'square-outline'} size={18} color={colors.primary as string} />
                    <View style={styles.userOptionText}>
                      <Text style={styles.typeOptionText} numberOfLines={1}>{isAll ? t('reports.filters.allUsers') : u.name}</Text>
                      {!!u.email && <Text style={styles.userOptionEmail} numberOfLines={1}>{u.email}</Text>}
                    </View>
                  </TouchableOpacity>
                );
              }}
            />
            <TouchableOpacity style={styles.pickerDoneBtn} onPress={() => { setShowUserPicker(false); setUserSearchQuery(''); }}>
              <Text style={styles.pickerDoneText}>
                {agentFilterCount > 0 ? t('reports.filters.doneCount', { count: agentFilterCount }) : t('reports.filters.done')}
              </Text>
            </TouchableOpacity>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

      {/* Saved reports picker */}
      <Modal visible={showSavedReports} transparent animationType="fade" onRequestClose={() => setShowSavedReports(false)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>{t('reports.saved.title')}</Text>
            {savedReports.length === 0 ? (
              <Text style={styles.emptyText}>{t('reports.saved.empty')}</Text>
            ) : (
              savedReports.map((r) => (
                <View key={r._id} style={[styles.savedRow, { flexDirection: rtl.rowDirection }]}>
                  <TouchableOpacity style={styles.savedRowMain} onPress={() => applySavedReport(r)}>
                    <Text style={styles.savedRowName}>{r.name}</Text>
                    <Text style={styles.savedRowMeta}>{r.reportType} {r.isTemplate ? `· ${t('reports.saved.template')}` : ''}</Text>
                  </TouchableOpacity>
                  {canEditSaved && (
                    <TouchableOpacity onPress={() => handleDeleteSavedReport(r._id)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                      <Ionicons name="trash-outline" size={18} color={colors.error as string} />
                    </TouchableOpacity>
                  )}
                </View>
              ))
            )}
            <TouchableOpacity style={styles.modalCloseBtn} onPress={() => setShowSavedReports(false)}>
              <Text style={styles.modalCloseBtnText}>{t('common.close')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Save-as dialog */}
      <Modal visible={showSaveDialog} transparent animationType="fade" onRequestClose={() => setShowSaveDialog(false)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>{t('reports.save.title')}</Text>
            <TextInput
              style={styles.textFilterInput}
              placeholder={t('reports.save.namePlaceholder')}
              placeholderTextColor={colors.text.disabled as string}
              value={saveName}
              onChangeText={setSaveName}
            />
            <TouchableOpacity style={[styles.templateToggle, { flexDirection: rtl.rowDirection }]} onPress={() => setSaveAsTemplate(!saveAsTemplate)}>
              <Ionicons name={saveAsTemplate ? 'checkbox' : 'square-outline'} size={20} color={colors.primary as string} />
              <Text style={styles.templateToggleText}>{t('reports.save.asTemplate')}</Text>
            </TouchableOpacity>
            <View style={[styles.modalButtonRow, { flexDirection: rtl.rowDirection }]}>
              <TouchableOpacity style={styles.modalCloseBtn} onPress={() => setShowSaveDialog(false)}>
                <Text style={styles.modalCloseBtnText}>{t('common.cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.modalSaveBtn} onPress={handleSaveReport}>
                <Text style={styles.modalSaveBtnText}>{t('common.save')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: { flex: 1 },
  titleRow: {
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.border as string,
  },
  title: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold' as const,
    color: colors.text.primary as string,
  },
  headerActions: { gap: spacing.sm },
  headerButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.background.secondary as string,
  },
  modalList: {
    // Bounded so a long option list (message types, or a Manager/Admin's agent
    // list) scrolls WITHIN the card instead of pushing past its maxHeight — the
    // previous plain View + .map() had no scroll container at all, so content
    // genuinely overflowed/got clipped by modalCard's maxHeight.
    maxHeight: 360,
  },
  searchInput: {
    height: 40,
    borderRadius: 8,
    paddingHorizontal: spacing.sm,
    backgroundColor: colors.background.secondary as string,
    color: colors.text.primary as string,
    fontSize: typography.fontSize.sm,
    marginBottom: spacing.xs,
  },
  userOptionText: { flex: 1, minWidth: 0 },
  userOptionEmail: {
    fontSize: 11,
    color: colors.text.secondary as string,
    marginTop: 1,
  },
  list: {
    padding: spacing.lg,
    gap: spacing.sm,
    paddingBottom: spacing.xxl ?? 48,
  },
  kpiLoading: {
    paddingVertical: spacing.lg,
    alignItems: 'center',
  },
  groupToggle: {
    marginStart: 'auto',
  },
  pickerSectionTitle: {
    color: colors.text.secondary,
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'uppercase',
    paddingHorizontal: spacing.sm,
    paddingTop: spacing.sm,
    paddingBottom: spacing.xs,
  },
  pickerDoneBtn: {
    alignSelf: 'flex-end',
    backgroundColor: colors.primary,
    borderRadius: spacing.borderRadius.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    marginTop: spacing.sm,
  },
  pickerDoneText: {
    color: colors.text.onPrimary,
    fontWeight: '600',
  },
  kpiRow: {
    gap: spacing.sm,
    marginBottom: spacing.md,
    flexWrap: 'wrap',
  },
  chartsRow: {
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  chartCard: {
    flex: 1,
    backgroundColor: colors.background.secondary as string,
    borderRadius: 14,
    padding: spacing.sm,
    minWidth: 160,
  },
  chartCardTitle: {
    fontSize: typography.fontSize.sm,
    fontWeight: '600' as const,
    color: colors.text.primary as string,
    marginBottom: spacing.xs,
  },
  tabRow: {
    gap: spacing.sm,
    paddingTop: spacing.sm,
    marginBottom: spacing.sm,
    flexWrap: 'wrap',
  },
  tabBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: spacing.borderRadius?.sm ?? 8,
    backgroundColor: colors.background.secondary as string,
  },
  tabBtnActive: {
    backgroundColor: (colors.primary as string) + '20',
  },
  tabBtnText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary as string,
  },
  tabBtnTextActive: {
    color: colors.primary as string,
    fontWeight: '600' as const,
  },
  filtersBar: {
    gap: spacing.xs,
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.sm,
    paddingBottom: spacing.sm,
    flexWrap: 'wrap',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: colors.border as string,
  },
  typePickerBtn: {
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 8,
    backgroundColor: colors.background.secondary as string,
    maxWidth: 180,
  },
  typePickerBtnText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.primary as string,
  },
  textFilterInput: {
    height: 36,
    borderRadius: 8,
    paddingHorizontal: spacing.sm,
    backgroundColor: colors.background.secondary as string,
    color: colors.text.primary as string,
    fontSize: typography.fontSize.sm,
  },
  centered: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    padding: spacing.lg,
  },
  emptyText: {
    fontSize: typography.fontSize.base,
    color: colors.text.secondary as string,
  },
  row: {
    alignItems: 'center',
    padding: spacing.sm,
    borderRadius: 12,
    gap: spacing.sm,
  },
  rowSelected: {
    backgroundColor: (colors.primary as string) + '15',
  },
  indexBadge: {
    minWidth: 22,
    height: 22,
    borderRadius: 11,
    paddingHorizontal: 4,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: (colors.text.disabled as string) + '20',
  },
  indexBadgeText: {
    fontSize: 11,
    fontWeight: '700' as const,
    color: colors.text.secondary as string,
  },
  rowInfo: { flex: 1, minWidth: 0 },
  rowPrimaryLine: {
    alignItems: 'center',
    gap: spacing.xs,
  },
  rowPrimary: {
    fontSize: typography.fontSize.base,
    fontWeight: '600' as const,
    color: colors.text.primary as string,
    flexShrink: 1,
  },
  countBadge: {
    backgroundColor: (colors.primary as string) + '20',
    borderRadius: 10,
    paddingHorizontal: 6,
    paddingVertical: 1,
  },
  countBadgeText: {
    fontSize: 10,
    fontWeight: '700' as const,
    color: colors.primary as string,
  },
  rowSecondary: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary as string,
    marginTop: 2,
  },
  rowAgentLine: {
    alignItems: 'center',
    gap: 4,
    marginTop: 2,
  },
  rowAgentText: {
    fontSize: 11,
    color: colors.text.disabled as string,
  },
  rowTrailing: {
    alignItems: 'flex-end',
    gap: 4,
  },
  rowTime: {
    fontSize: 11,
    color: colors.text.disabled as string,
  },
  openConversationIconBtn: {
    padding: 2,
  },
  mutedCell: {
    fontSize: typography.fontSize.sm,
    color: colors.text.disabled as string,
  },
  inlineActionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  inlineActionText: {
    fontSize: typography.fontSize.sm,
    color: colors.primary as string,
  },
  recordingCellReady: {
    width: 220,
  },
  avatarWrap: {
    position: 'relative',
  },
  avatarFallback: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarFallbackText: {
    fontWeight: 'bold' as const,
  },
  avatarChannelBadge: {
    position: 'absolute',
    bottom: -2,
    right: -2,
    width: 16,
    height: 16,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: colors.background.primary as string,
  },
  detailsPanel: {
    flex: 1,
    padding: spacing.lg,
  },
  detailsPanelHeader: {
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.md,
  },
  detailsPanelName: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold' as const,
    color: colors.text.primary as string,
    textAlign: 'center',
  },
  detailsPanelSub: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary as string,
  },
  openConversationBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: colors.primary as string,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: 10,
    marginTop: spacing.xs,
  },
  openConversationBtnText: {
    color: '#FFFFFF',
    fontSize: typography.fontSize.sm,
    fontWeight: '600' as const,
  },
  detailsPanelDivider: {
    height: 1,
    backgroundColor: colors.border as string,
    marginVertical: spacing.sm,
  },
  detailsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.xs,
  },
  detailsLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary as string,
  },
  detailsValue: {
    fontSize: typography.fontSize.sm,
    fontWeight: '600' as const,
    color: colors.text.primary as string,
  },
  detailsSecondaryLine: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary as string,
    marginTop: 2,
  },
  detailsChannelChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
    borderRadius: 10,
  },
  detailsChannelChipText: {
    fontSize: 11,
    fontWeight: '600' as const,
    textTransform: 'capitalize',
  },
  detailsContent: {
    fontSize: typography.fontSize.sm,
    color: colors.text.primary as string,
    marginTop: spacing.xs,
    lineHeight: 20,
  },
  detailsEmpty: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    padding: spacing.lg,
  },
  detailsEmptyText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.disabled as string,
    textAlign: 'center',
  },
  // Same backdrop and card as global search and the filter panels.
  modalOverlay: overlayPickerStyles().overlay,
  modalCard: { ...overlayPickerStyles().card, padding: spacing.lg, gap: spacing.sm },
  modalTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold' as const,
    color: colors.text.primary as string,
    marginBottom: spacing.sm,
  },
  typeOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border as string,
  },
  typeOptionText: {
    fontSize: typography.fontSize.base,
    color: colors.text.primary as string,
    textTransform: 'capitalize',
  },
  savedRow: {
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border as string,
  },
  savedRowMain: { flex: 1 },
  savedRowName: {
    fontSize: typography.fontSize.base,
    fontWeight: '600' as const,
    color: colors.text.primary as string,
  },
  savedRowMeta: {
    fontSize: 11,
    color: colors.text.secondary as string,
    marginTop: 2,
  },
  templateToggle: {
    alignItems: 'center',
    gap: spacing.xs,
    marginTop: spacing.xs,
  },
  templateToggleText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.primary as string,
  },
  modalButtonRow: {
    gap: spacing.sm,
    marginTop: spacing.md,
  },
  modalCloseBtn: {
    flex: 1,
    paddingVertical: spacing.sm,
    borderRadius: 10,
    backgroundColor: colors.background.secondary as string,
    alignItems: 'center',
  },
  modalCloseBtnText: {
    color: colors.text.primary as string,
    fontWeight: '600' as const,
  },
  modalSaveBtn: {
    flex: 1,
    paddingVertical: spacing.sm,
    borderRadius: 10,
    backgroundColor: colors.primary as string,
    alignItems: 'center',
  },
  modalSaveBtnText: {
    color: '#FFFFFF',
    fontWeight: 'bold' as const,
  },
});
